import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services';


@Injectable()
export class ApiInterceptor implements HttpInterceptor {

  // "View as [Role]": identity/bootstrap endpoints that must keep answering as the REAL admin even
  // while a role preview is active - the profile restore validates against the real role, the
  // role list is the preview's exit path, and login history should record the real role.
  private static readonly roleViewExemptPaths: string[] = [
    '/user/me',
    '/lookup/my-features',
    '/lookup/my-menus',
    '/role-admin',
    '/activity/heartbeat'
  ];

  constructor(public authService: AuthService) { }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // While an Admin previews a role, tag API DATA requests with the previewed role so the server
    // scopes authorization + role-scoped reads to it (the X-View-As-Role middleware; ignored
    // server-side for anyone who is not really an Admin).
    request = this.withRoleView(request);

    // SSO mode: acquire the Entra access token asynchronously, then attach it.
    if (this.authService.isSsoEnabled) {
      return this.authService.getSsoAccessToken().pipe(
        switchMap(token => {
          const authReq = request.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
          return next.handle(authReq);
        })
      );
    }

    // JWT mode (unchanged): attach the token synchronously from local storage.
    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${this.authService.getToken()}`
      },
      withCredentials: true
    });

    return next.handle(request);
  }

  private withRoleView(request: HttpRequest<any>): HttpRequest<any> {
    const view = this.authService.activeRoleView;
    if (!view || !this.authService.isUserAdmin()) { return request; }
    if (!request.url.startsWith(environment.adminApiUrl)) { return request; }

    const path = request.url.substring(environment.adminApiUrl.length);
    if (ApiInterceptor.roleViewExemptPaths.some(p => path.startsWith(p))) { return request; }

    return request.clone({ setHeaders: { 'X-View-As-Role': String(view.roleId) } });
  }
}
